// EMCON-stripa: nivået som gjelder, umulig å overse.
//
// ── HVORFOR DENNE FILA FINNES ───────────────────────────────────────────────
// `emcon.ts` (policy-motoren) har hatt null kallsteder siden den ble skrevet.
// Hubben eier nå nivået (`emcon-lager.ts`), men et nivå ingen SER er ingen ordre.
//
// ⚠ EMCON HÅNDHEVES I KLIENTEN — DERFOR ER VISNINGEN SELVE MEKANISMEN.
// For tilgangskontroll er klient-gating aldri nok (§4), fordi trusselen er en
// klient som lyver. EMCON er motsatt: det som skal kontrolleres er senderen i
// apparatet, og hubben kan ikke hindre noe fra å stråle. Stripa er derfor ikke
// pynt over en backend-regel — den er hvordan regelen virker i det hele tatt.
//
// ⚠ RØDT ER RESERVERT HOSTIL (UX §1). Radiotaushet er ikke en fiende, den er en
// ORDRE. Gull er systemets ene «se hit»-farge og brukes her; det strengeste
// nivået skiller seg ved å være FYLT framfor ved å bytte farge.
import type { EmconLevel } from '@skade/contracts';

export interface EmconVisning {
  /** Kort tekst i stripa. */
  tekst: string;
  /** Fyldig forklaring til `title` — grunn, avsender og gjenstående tid. */
  detalj: string;
  /** `stille` = restriktivt nivå (vis stripa), `normal` = skjul den. */
  klasse: 'stille' | 'streng' | 'normal';
  /** Skal stripa vises i det hele tatt? */
  vis: boolean;
}

const NIVAA_KORT: Record<EmconLevel, string> = {
  alpha: 'EMCON ALPHA — FULL RADIOTAUSHET',
  bravo: 'EMCON BRAVO — kun nød og ordre',
  charlie: 'EMCON CHARLIE — begrenset',
  delta: 'EMCON DELTA — normal drift',
};

/**
 * Hva stripa skal si.
 *
 * ⚠ `delta` SKJULER STRIPA. En permanent «alt er normalt»-linje er den raskeste
 * veien til at ingen leser stripa den dagen den sier noe annet. Den skal bare
 * finnes når det er noe å etterleve.
 */
export function emconVisning(
  status: {
    nivaa?: unknown; ordre?: { satAv?: unknown; grunn?: unknown; gjelderTilMs?: unknown } | null;
    gjelder?: unknown; lesefeil?: unknown;
  } | null,
  naa: number,
): EmconVisning {
  const raa = typeof status?.nivaa === 'string' ? status.nivaa : 'delta';
  const nivaa = (['alpha', 'bravo', 'charlie', 'delta'].includes(raa) ? raa : 'delta') as EmconLevel;

  // ⚠ EN LESEFEIL I HUBBEN SKAL SES, MEN SKAL IKKE PÅSTÅ RADIOTAUSHET.
  // `emcon-lager.ts` faller til normal drift ved korrupt fil — å vise «alpha»
  // her ville lammet avdelingen på grunn av en diskfeil. Vi viser feilen, ikke
  // et nivå ingen har bestemt.
  if (status?.lesefeil === true) {
    return {
      tekst: '⚠ EMCON-status kunne ikke leses i hubben',
      detalj: 'Hubbens EMCON-fil kunne ikke leses, så nivået er ukjent og behandles '
        + 'som normal drift. Sett nivået på nytt for å være sikker.',
      klasse: 'stille', vis: true,
    };
  }

  if (nivaa === 'delta') {
    return { tekst: NIVAA_KORT.delta, detalj: 'Ingen sendebegrensning.', klasse: 'normal', vis: false };
  }

  const o = status?.ordre ?? null;
  const satAv = typeof o?.satAv === 'string' ? o.satAv : '';
  const grunn = typeof o?.grunn === 'string' ? o.grunn.trim() : '';
  const til = typeof o?.gjelderTilMs === 'number' ? o.gjelderTilMs : undefined;

  const deler: string[] = [];
  if (satAv) deler.push(`Satt av ${satAv}`);
  if (grunn) deler.push(grunn);
  // ⚠ EN STÅENDE ORDRE MERKES SOM DET. Glemt radiotaushet er en avdeling som
  // ikke kan rapportere når det gjelder, og den som satte den er kanskje av vakt.
  if (til === undefined) deler.push('⚠ Står til den endres — ingen utløpstid');
  else if (til > naa) deler.push(`Gjelder ${Math.max(1, Math.round((til - naa) / 60_000))} min til`);
  else deler.push('Utløpt');

  // Nødtrafikk slipper alltid gjennom, og det skal operatøren VITE — ellers
  // holder hun tilbake en nødmelding fordi hun tror alt er sperret.
  deler.push('Nødtrafikk slipper alltid gjennom.');

  return {
    tekst: NIVAA_KORT[nivaa],
    detalj: deler.join(' · '),
    klasse: nivaa === 'alpha' ? 'streng' : 'stille',
    vis: true,
  };
}

/**
 * Stripa som DOM. Ett element, gjenbrukt — ikke bygget på nytt per oppdatering.
 */
export class EmconStripe {
  readonly el: HTMLElement;

  constructor() {
    this.el = document.createElement('div');
    this.el.className = 'emcon-stripe';
    this.el.hidden = true;
    this.el.setAttribute('role', 'status');
    // ⚠ `aria-live="assertive"`: en EMCON-endring skal leses opp umiddelbart av
    // en skjermleser, ikke ved neste naturlige pause. Nivået styrer om noen får
    // sende i det hele tatt.
    this.el.setAttribute('aria-live', 'assertive');
  }

  oppdater(status: unknown, naa = Date.now()): EmconVisning {
    const v = emconVisning(status as never, naa);
    this.el.hidden = !v.vis;
    this.el.textContent = v.tekst;
    this.el.title = v.detalj;
    this.el.classList.toggle('streng', v.klasse === 'streng');
    return v;
  }
}
